import { useId, useRef, useState } from 'react';
import { useClickAway } from 'ahooks';
import map from 'lodash/map';

import { Box, Icon, Input, LabeledInput } from '@zillow/constellation-10';
import { IconClockOutline } from '@zillow/constellation-icons';

import constants from '../../constants/ConstantsBundle';

// Styles
import { autocompleteContainerStyles, dropDownItemRecipe, dropdownItemTextStyles, dropdownStyles } from './styles';

// types
import type AutocompleteAddressModelType from '../../models/AutocompleteAddress';
import type { FormEvent, KeyboardEventHandler, ReactNode } from 'react';

export type AugmentedAutocompleteAddressType = AutocompleteAddressModelType & {
  isRecent?: boolean;
  unit?: string;
};

// const logger = getLogger('components/autocomplete');

const Autocomplete = ({
  items,
  onChange,
  onSelect,
  placeholder = 'Search',
  value = '',
  error = false,
  label,
  inputId,
  description,
}: {
  items: Array<AugmentedAutocompleteAddressType>;
  onChange: (query: string) => void;
  onSelect: (selection: AugmentedAutocompleteAddressType) => void;
  placeholder?: string;
  value: string;
  label?: ReactNode;
  inputId?: string;
  description?: ReactNode;
  error?: boolean;
}) => {
  const [isFocused, toggleFocus] = useState(false);
  const [activeIndex, updateActiveIndex] = useState(-1);
  const listboxId = useId();
  const descriptionId = useId();
  const isOpen = isFocused && items.length > 0;
  const getOptionId = (idx: number) => `${listboxId}-option-${idx}`;

  const ref = useRef(null);

  useClickAway(() => toggleFocus(false), ref);

  const handleFocus = (): void => {
    toggleFocus(true);
  };

  const handleChange = (e: FormEvent<HTMLInputElement>): void => {
    // need e.persist() so that the debounced change handler has reference to the event
    // https://reactjs.org/docs/events.html#event-pooling
    e.persist();

    // @ts-ignore https://github.com/desktop/desktop/issues/297
    onChange(e.target.value);
  };

  const handleSelect = (item: AugmentedAutocompleteAddressType): void => {
    onSelect(item);
    toggleFocus(false);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    const keyCode = e.which;
    toggleFocus(true);

    if (keyCode === constants.UP_ARROW_KEYCODE) {
      const upOneIndex = activeIndex === -1 ? items.length - 1 : activeIndex - 1;
      updateActiveIndex(upOneIndex);

      e.preventDefault();
    } else if (keyCode === constants.DOWN_ARROW_KEYCODE) {
      const downOneIndex = activeIndex === items.length - 1 ? -1 : activeIndex + 1;
      updateActiveIndex(downOneIndex);

      e.preventDefault();
    } else if (keyCode === constants.ESC_KEYCODE) {
      toggleFocus(false);
    } else if (keyCode === constants.TAB_KEYCODE) {
      toggleFocus(false);
    } else if (keyCode === constants.ENTER_KEYCODE) {
      e.preventDefault();
      // if no activeIndex, default to first selection

      const selectedItem = activeIndex === -1 ? items[0] : items[activeIndex];

      if (selectedItem) {
        handleSelect(selectedItem);

        // @ts-ignore https://github.com/desktop/desktop/issues/297
        e.target.blur();
      } else {
        // logger.warn('User did not select an option.');
      }
    }
  };

  const renderDropdown = (): ReactNode => {
    if (!isOpen) {
      return null;
    }
    return (
      <Box id={listboxId} role="listbox" css={dropdownStyles}>
        {map(items, (item, idx) => renderDropdownItem({ item, idx }))}
      </Box>
    );
  };

  const renderDropdownItem = ({ idx, item }: { idx: number; item: AugmentedAutocompleteAddressType }): ReactNode => {
    return (
      <Box
        id={getOptionId(idx)}
        role="option"
        aria-selected={idx === activeIndex}
        css={dropDownItemRecipe.raw({ isActive: idx === activeIndex })}
        key={idx}
        onClick={() => handleSelect(items[idx])}
        onMouseEnter={() => updateActiveIndex(idx)}
        onMouseMove={() => updateActiveIndex(idx)}
      >
        <Box css={dropdownItemTextStyles} key={idx}>
          {item.isRecent && (
            <Icon size="sm" css={{ marginRight: '400' }}>
              <IconClockOutline />
            </Icon>
          )}
          {item.name}
        </Box>
      </Box>
    );
  };

  const renderInput = () => (
    <Input
      {...(inputId && { id: inputId })}
      role="combobox"
      aria-autocomplete="list"
      aria-expanded={isOpen}
      aria-controls={isOpen ? listboxId : undefined}
      aria-activedescendant={isOpen && activeIndex !== -1 ? getOptionId(activeIndex) : undefined}
      aria-describedby={description ? descriptionId : undefined}
      autoComplete="off"
      onChange={handleChange}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      value={value}
      error={error}
    />
  );

  /**
   * Note about disabling chrome's autocomplete:
   * autocomplete="off | false" prop on <input> should work, however Foundation's
   * Input does not pass `...rest` to the <input>. Luckily, you can disable
   * autocomplete for an entire form in the same way, so a somewhat hacky
   * workaround is to wrap the Input in a form. The 'data-' prefix used to embed
   * custom attributes on HTML elements and fix typescript errors.
   */
  return (
    <Box css={autocompleteContainerStyles}>
      <form ref={ref} data-autocomplete="off">
        {label ? <LabeledInput label={label} input={renderInput()} /> : renderInput()}

        {renderDropdown()}
      </form>
      {description && <Box id={descriptionId}>{description}</Box>}
    </Box>
  );
};

export default Autocomplete;
